import React, { useState } from "react";
import { useWeb3 } from "../context/Web3Context";
import Button from "../components/Common/Button";
import { Card } from "../components/Common/Card";
import { shortAddress } from "../utils/format";
import AdminUsers from "./admin/AdminUsers";
import AdminWallets from "./admin/AdminWallets";
import AdminSystem from "./admin/AdminSystem";
import AdminHistory from "./admin/AdminHistory";
import AdminAnnouncements from "./admin/AdminAnnouncements";
import AdminUserList from "./admin/AdminUserList";
import AdminRanks from "./admin/AdminRanks";
import AdminRewards from "./admin/AdminRewards";
import AdminLinking from "./admin/AdminLinking";

function Admin() {
  const { account, connect, isCorrectChain, isOwner, isAdmin, hasAdminAccess } = useWeb3();
  const [tab, setTab] = useState("users");

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">🔌</div>
        <div className="empty-state-text mb-lg">Connect wallet to access contract panel.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }

  if (!isCorrectChain) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">⚠</div>
        <div className="empty-state-text">Wrong network. Switching...</div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">🔒</div>
        <div className="empty-state-text mb-lg">
          Access denied. Only the contract owner or manager can view this page.
        </div>
        <div className="mono text-muted" style={{ fontSize: 12 }}>
          Connected: {shortAddress(account, 6)}
        </div>
      </div>
    );
  }

  // Build tab list — system + linking are owner-only
  const tabs = [
    { id: "users", label: "Users" },
    { id: "userlist", label: "User List" },
    { id: "ranks", label: "Ranks" },
    { id: "rewards", label: "Rewards" },
    { id: "history", label: "History" },
    { id: "news", label: "News" },
    { id: "wallets", label: "Wallets" },
    ...(isOwner ? [{ id: "linking", label: "Linking" }] : []),
    ...(isOwner ? [{ id: "system", label: "System" }] : []),
  ];

  // If admin switched to a tab that's no longer allowed, snap back to Users
  const activeTab = tabs.find((t) => t.id === tab) ? tab : "users";

  return (
    <div className="animate-fade-in-up">
      {/* Role banner */}
      <Card glow className="mb-lg">
        <div className="flex justify-between items-center">
          <div>
            <div className="stat-card-label">Contract Panel</div>
            <div style={{ fontSize: 18, fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text-primary)" }}>
              {isOwner ? "Owner Access" : "Manager Access"}
            </div>
          </div>
          <span className="badge badge-cyan">
            {isOwner ? "OWNER" : "MANAGER"}
          </span>
        </div>
      </Card>

      {/* Tabs */}
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${activeTab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "users" && <AdminUsers />}
      {activeTab === "userlist" && <AdminUserList />}
      {activeTab === "ranks" && <AdminRanks />}
      {activeTab === "rewards" && <AdminRewards />}
      {activeTab === "history" && <AdminHistory />}
      {activeTab === "news" && <AdminAnnouncements />}
      {activeTab === "wallets" && <AdminWallets />}
      {activeTab === "linking" && isOwner && <AdminLinking />}
      {activeTab === "system" && isOwner && <AdminSystem />}
    </div>
  );
}

export default Admin;
